import { GOOGLE_CLASSROOM_TILES } from './../../shared/constants/google-classroom.constant';
import { REMOTE_INTERACTION_TILES } from './../../shared/constants/remote-interaction.constant';
import { Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { groupBy, toArray } from 'lodash';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { SpinnerModal } from 'Src/app/shared/modals/status-spinner/status-spinner.service';
import { Toggles } from 'Src/ng2/shared/constants/toggles.constant';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { UtilitiesService } from 'Src/ng2/shared/services/utilities/utilities.service';
import { ISchool } from 'Src/ng2/shared/typings/interfaces/school.interface';
import { IUser } from 'Src/ng2/shared/typings/interfaces/user.interface';
import { ResetFocus } from 'Src/ng2/store/actions/focus-actions/focus-actions';
import { NYCT_DASH_TILES } from '../../shared/constants/nyct-dash.constant';
import { ITile, TValidTileCats } from './interfaces/dashboard.interfaces';
import { TileDataService } from './tile-data/tile-data.service';

interface ITileByCategory {
  cat: TValidTileCats;
  tiles: ITile[];
}

@Component({
  selector: 'tiles',
  template: require('./tiles.component.html'),
  styles: [require('./tiles.scss')],
})
export class TilesComponent implements OnInit {
  @ViewChild('dashContainer') dashContainer: ElementRef;
  @Input() currentSchool: ISchool;
  @Input() currentUser: IUser;

  tileData$: Observable<ITileByCategory[]>;
  oldTileStateByGraphKey: { [key: string]: object };
  setPanelShadowStyle: boolean = false;
  isNYCTToggleOn: boolean = false;
  isRemoteInteractionToggleOn: boolean = false;
  isGoogleClassroomToggleOn: boolean = false;

  constructor(
    @Inject('$state') private $state,
    @Inject('SpinnerModal') private spinnerModal: SpinnerModal,
    private store: Store<any>,
    private tileDataService: TileDataService,
    private utilitiesService: UtilitiesService,
    private imUser: ImUser,
    private toggleService: ToggleService,
  ) {}

  ngOnInit(): void {
    this.spinnerModal.activateSpinner('Loading...');
    const isCaseloadUser = this.imUser.isCaseloadUser(this.currentUser);
    this.tileData$ = this.initializeTileData(this.currentSchool, isCaseloadUser);
    this.isNYCTToggleOn = this.toggleService.getToggleState(Toggles.TOGGLE_NYCT_MVP);
    this.isRemoteInteractionToggleOn = this.toggleService.getToggleState(Toggles.TOGGLE_REMOTE_INTERACTION);
    this.isGoogleClassroomToggleOn = this.toggleService.getToggleState(Toggles.TOGGLE_GOOGLE_CLASSROOM);
  }

  initializeTileData(currentSchool: ISchool, isCaseloadUser: boolean): Observable<ITileByCategory[]> {
    let tileData: Observable<ITileByCategory[]>;
    // currently we do not want caseload users seeing dashboard graphs
    if (isCaseloadUser) {
      tileData = null;
      this.spinnerModal.deactivateSpinner();
      this.$state.go('main.home');
    } else {
      tileData = this.tileDataService.getTileData$(currentSchool._id).pipe(
        map(tileData => {
          // Remove NYCT tiles if not NYCT school
          if (!this.isNYCTToggleOn) {
            tileData = tileData.filter(({ tileName }) => !NYCT_DASH_TILES[tileName]);
          }
          // Remove Remove Interaction tiles if not NV Charter school
          if (!this.isRemoteInteractionToggleOn) {
            const remoteInteractionLastDayRegex = /(Interaction rate for)/; // tile name is dynamic, so use regex
            tileData = tileData.filter(
              ({ tileName }) => !REMOTE_INTERACTION_TILES[tileName] && !tileName.match(remoteInteractionLastDayRegex),
            );
          }
          // Remove Google Classroom (Device Access) tiles if not NV Affinity school
          if (!this.isGoogleClassroomToggleOn) {
            tileData = tileData.filter(({ tileName }) => !GOOGLE_CLASSROOM_TILES[tileName]);
          }
          // Remove Gradebook tile if charter school
          if (currentSchool.isNVCMO) {
            tileData = tileData.filter(({ graphQlKeyFocus }) => {
              return graphQlKeyFocus !== 'NUMBER_NOT_PASSING_NEEDED_FOR_EXEMPTION_GB_SOURCE';
            });
          }
          const sortedTiles = this.sortTilesByTileOrder(tileData);
          const groupedTiles = this.groupTilesByCategory(sortedTiles);
          const sortedGroups = this.sortGroupsByCategoryOrder(groupedTiles);
          return sortedGroups;
        }),
        tap(() => this.spinnerModal.deactivateSpinner()),
      );
    }
    return tileData;
  }

  sortTilesByTileOrder(tileData: ITile[]): ITile[] {
    tileData.sort((a, b) => {
      return a.tileOrder - b.tileOrder;
    });
    return tileData;
  }

  groupTilesByCategory(tileData): ITileByCategory[] {
    const groupedTiles = toArray(groupBy(tileData, 'category'));
    return groupedTiles.reduce((accum, group) => {
      const { category } = group[0];
      const formattedCategory = category.replace(/_/g, ' ');
      accum.push({ cat: formattedCategory, tiles: group });
      return accum;
    }, []);
  }

  sortGroupsByCategoryOrder(tileData: ITileByCategory[]): ITileByCategory[] {
    tileData.sort((a, b) => {
      const catA = a.tiles[0];
      const catB = b.tiles[0];
      return catA.categoryOrder - catB.categoryOrder;
    });
    return tileData;
  }

  goToMidLevel(tile: ITile): void {
    const { graphQlKeyFocus, defaultGraphQlKeyFilter, defaultGraphQlKeyGrouping } = tile;
    this.store.dispatch(new ResetFocus());
    this.$state.go(
      'main.school.lists.midLevel',
      { focus: graphQlKeyFocus, filter: defaultGraphQlKeyFilter, grouping: defaultGraphQlKeyGrouping },
      { location: 'replace' },
    );
  }

  onListScroll(): void {
    this.setPanelShadowStyle = this.utilitiesService.showListPanelShadow(this.dashContainer.nativeElement.scrollTop);
  }
}
